import type { HistoricalTimelineDecade } from "../generated-types";
import { imageSizes } from "../image-sizes";
import {
	formatMetricAverage,
	getDecadeMetricAverage,
	getDecadeMetricSeries,
} from "./decade-hover-graphs";
import { getDecadeHoverImageUrl, escapeHtmlAttribute } from "./timeline-utils";

export type DecadeColumnLabels = {
	explore?: string;
	pricePerBarrel?: string;
	population?: string;
	select?: string;
	emptyYearsMessage?: string;
	resetFilters?: string;
	event?: string;
	eventsInYear?: string;
};

const DEFAULT_EMPTY_YEARS_MESSAGE =
	"No years found with the current filter. Try again with another filter or clear the search.";

export function createEmptyYearsStateElement(
	onReset: () => void,
	labels: Pick<DecadeColumnLabels, "emptyYearsMessage" | "resetFilters"> = {}
): HTMLElement {
	const root = document.createElement("div");
	root.className = "historical-timeline__empty-years";

	const message = document.createElement("p");
	message.className = "historical-timeline__empty-years-message";
	message.textContent = labels.emptyYearsMessage ?? DEFAULT_EMPTY_YEARS_MESSAGE;

	const resetButton = document.createElement("button");
	resetButton.type = "button";
	resetButton.className = "historical-timeline__empty-years-reset";
	resetButton.textContent = labels.resetFilters ?? "Clear filters";
	resetButton.addEventListener("click", (event) => {
		event.stopPropagation();
		onReset();
	});

	root.append(message, resetButton);

	return root;
}

const OIL_BARREL_ICON = `<svg xmlns="http://www.w3.org/2000/svg" width="34" height="40" viewBox="0 0 34 40" fill="none" aria-hidden="true">
  <path d="M29.316 6.20057V34.7965C29.316 35.7462 28.9354 36.6569 28.258 37.3285C27.5805 38 26.6617 38.3773 25.7037 38.3773H8.16988C7.21185 38.3773 6.29306 38 5.61562 37.3285C4.93819 36.6569 4.55762 35.7462 4.55762 34.7965V6.16992" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M4.55762 6.17026V1.62305H29.316V6.19911" stroke="currentColor" stroke-width="2" stroke-miterlimit="10"/>
  <path d="M1.63672 1.62305H32.3628" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M2.91895 10.791H30.9694" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M2.91895 20.0293H30.9694" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M2.91895 29.249H30.9694" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`;

const POPULATION_ICON = `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40" fill="none" aria-hidden="true">
  <g clip-path="url(#clip0_19510_164474)">
    <path d="M9.34866 17.2231C12.2352 17.2231 14.5753 14.8636 14.5753 11.9529C14.5753 9.04219 12.2352 6.68262 9.34866 6.68262C6.4621 6.68262 4.12207 9.04219 4.12207 11.9529C4.12207 14.8636 6.4621 17.2231 9.34866 17.2231Z" stroke="currentColor" stroke-width="2" stroke-miterlimit="10" stroke-linecap="round"/>
    <path d="M1.40039 38.5882V25.9484C1.40039 25.5046 1.48709 25.0652 1.65555 24.6553C1.824 24.2453 2.0709 23.8729 2.38215 23.5592C2.6934 23.2455 3.06289 22.9967 3.46952 22.827C3.87614 22.6573 4.31194 22.5701 4.75201 22.5703H11.9189" stroke="currentColor" stroke-width="2" stroke-miterlimit="10" stroke-linecap="round"/>
    <path d="M30.6504 11.9526C33.537 11.9526 35.877 9.59305 35.877 6.68237C35.877 3.77168 33.537 1.41211 30.6504 1.41211C27.7639 1.41211 25.4238 3.77168 25.4238 6.68237C25.4238 9.59305 27.7639 11.9526 30.6504 11.9526Z" stroke="currentColor" stroke-width="2" stroke-miterlimit="10"/>
    <path d="M29.2002 17.4053H35.2484C36.1368 17.4053 36.9889 17.7612 37.6172 18.3947C38.2455 19.0282 38.5984 19.8874 38.5984 20.7833V38.5883" stroke="currentColor" stroke-width="2" stroke-miterlimit="10" stroke-linecap="round"/>
    <path d="M21.2641 25.2377C23.3806 25.2377 25.0965 23.5075 25.0965 21.3732C25.0965 19.239 23.3806 17.5088 21.2641 17.5088C19.1475 17.5088 17.4316 19.239 17.4316 21.3732C17.4316 23.5075 19.1475 25.2377 21.2641 25.2377Z" stroke="currentColor" stroke-width="2" stroke-miterlimit="10" stroke-linecap="round"/>
    <path d="M13.2979 38.5875V33.8005C13.2979 32.9042 13.651 32.0446 14.2795 31.4108C14.9081 30.777 15.7606 30.4209 16.6495 30.4209H25.8812C26.3213 30.4209 26.757 30.5083 27.1636 30.6782C27.5701 30.848 27.9395 31.097 28.2506 31.4109C28.5617 31.7247 28.8084 32.0973 28.9767 32.5073C29.145 32.9173 29.2315 33.3568 29.2313 33.8005V38.5875" stroke="currentColor" stroke-width="2" stroke-miterlimit="10" stroke-linecap="round"/>
  </g>
  <defs>
    <clipPath id="clip0_19510_164474">
      <rect width="40" height="40" fill="currentColor"/>
    </clipPath>
  </defs>
</svg>`;

export type DecadeColumnMarkupParams = {
	decade: HistoricalTimelineDecade;
	decadeIndex: number;
	labels: DecadeColumnLabels;
};

export type DecadeColumnElements = {
	column: HTMLElement;
	defaultDivider: HTMLElement;
	defaultBody: HTMLElement;
	title: HTMLElement;
	hoverCard: HTMLElement;
	exploreLabel: HTMLElement;
	hoverDotsHost: HTMLElement;
	graphsHost: HTMLElement;
	factsHost: HTMLElement;
	selectLabel: HTMLElement;
	selectContent: HTMLElement;
};

function buildHoverImageSrc(imageUrl: string, cropWidth: number, cropHeight: number): string {
	const url = new URL(imageUrl, window.location.href);

	url.searchParams.delete("hash");
	url.searchParams.set("cw", String(cropWidth));
	url.searchParams.set("ch", String(cropHeight));

	if (!url.searchParams.get("cx")) {
		url.searchParams.set("cx", "0.5");
	}

	if (!url.searchParams.get("cy")) {
		url.searchParams.set("cy", "0.5");
	}

	return `${url.pathname}${url.search}`;
}

function getHoverImageAttributes(imageUrl: string, imageAlt?: string) {
	const { sm, md, lg, xl } = imageSizes.historicalTimelineHover;
	const srcset = [sm, md, lg, xl]
		.map(({ cw, ch }) => `${buildHoverImageSrc(imageUrl, cw, ch)} ${cw}w`)
		.join(", ");
	const src = buildHoverImageSrc(imageUrl, sm.cw, sm.ch);

	return {
		src,
		srcset,
		alt: imageAlt ?? "",
	};
}

function getHoverImageMarkup(imageUrl?: string, imageAlt?: string): string {
	if (!imageUrl) {
		return "";
	}

	const { src, srcset, alt } = getHoverImageAttributes(imageUrl, imageAlt);

	return `
		<div class="historical-timeline__decade-hover-media">
			<img
				class="historical-timeline__decade-hover-image"
				src="${src}"
				srcset="${srcset}"
				sizes="(min-width: 1366px) 40vw, (min-width: 992px) 45vw, (min-width: 768px) 50vw, 80vw"
				alt="${alt}"
				loading="lazy"
			/>
		</div>
	`.trim();
}

export function syncHoverImageMedia(
	hoverCard: HTMLElement,
	imageUrl?: string,
	imageAlt?: string
): boolean {
	const validImageUrl = imageUrl?.trim();
	const existingImage = hoverCard.querySelector<HTMLImageElement>(".historical-timeline__decade-hover-image");

	if (!validImageUrl) {
		const hadMedia = Boolean(existingImage);
		hoverCard.querySelector(".historical-timeline__decade-hover-media")?.remove();
		return hadMedia;
	}

	const { src, srcset, alt } = getHoverImageAttributes(validImageUrl, imageAlt);

	if (existingImage) {
		const srcChanged = existingImage.getAttribute("src") !== src;
		const srcsetChanged = existingImage.getAttribute("srcset") !== srcset;
		const altChanged = existingImage.alt !== alt;

		if (!srcChanged && !srcsetChanged && !altChanged) {
			return false;
		}

		existingImage.src = src;
		existingImage.srcset = srcset;
		existingImage.alt = alt;
		return false;
	}

	const markup = getHoverImageMarkup(validImageUrl, imageAlt);
	const overlay = hoverCard.querySelector(".historical-timeline__decade-hover-overlay");
	if (overlay) {
		overlay.insertAdjacentHTML("beforebegin", markup);
	} else {
		hoverCard.insertAdjacentHTML("afterbegin", markup);
	}

	return true;
}

export function getFactRowMarkup(
	value: string | null | undefined,
	label: string,
	iconMarkup: string,
	variant: "price" | "population"
): string {
	if (value === null || value === undefined || value.trim() === "") {
		return "";
	}

	return `
		<div class="historical-timeline__fact-row historical-timeline__fact-row--${variant}" data-fact-row="true">
			<div class="historical-timeline__fact-copy">
				<p class="historical-timeline__fact-value">${value}</p>
				<p class="historical-timeline__fact-label">${label}</p>
			</div>
			<div class="historical-timeline__fact-icon-wrap historical-timeline__fact-icon-wrap--${variant}">${iconMarkup}</div>
		</div>
	`.trim();
}

export function getFactsMarkup(
	pricePerBarrel: string | null | undefined,
	population: string | null | undefined,
	labels: DecadeColumnLabels
): string {
	return [
		getFactRowMarkup(
			pricePerBarrel,
			labels.pricePerBarrel ?? "Price per Barrel of oil (USD)",
			OIL_BARREL_ICON,
			"price"
		),
		getFactRowMarkup(
			population,
			labels.population ?? "Saudi Arabia, Population (M)",
			POPULATION_ICON,
			"population"
		),
	].join("");
}

function getDetailCardFactRowMarkup(
	value: string,
	label: string,
	variant: "price" | "population"
): string {
	return `
		<div class="historical-timeline__detail-card-fact historical-timeline__detail-card-fact--${variant}" data-fact-row="true">
			<div class="historical-timeline__detail-card-fact-copy">
				<p class="historical-timeline__detail-card-fact-label">${escapeHtmlAttribute(label)}</p>
			</div>
			<p class="historical-timeline__detail-card-fact-value">${escapeHtmlAttribute(value)}</p>
		</div>
	`.trim();
}

export function getDetailCardFactsMarkup(
	decade: HistoricalTimelineDecade,
	activeCategoryKeys: string[],
	labels: DecadeColumnLabels
): string {
	const priceSeries = getDecadeMetricSeries(decade, activeCategoryKeys, "pricePerBarrel");
	const populationSeries = getDecadeMetricSeries(decade, activeCategoryKeys, "population");
	const priceAverage = getDecadeMetricAverage(priceSeries);
	const populationAverage = getDecadeMetricAverage(populationSeries);

	const rows: string[] = [];

	if (priceAverage !== null) {
		rows.push(
			getDetailCardFactRowMarkup(
				formatMetricAverage(priceAverage),
				labels.pricePerBarrel ?? "Price per Barrel of oil (USD)",
				"price"
			)
		);
	}

	if (populationAverage !== null) {
		rows.push(
			getDetailCardFactRowMarkup(
				formatMetricAverage(populationAverage),
				labels.population ?? "Saudi Arabia, Population (M)",
				"population"
			)
		);
	}

	return rows.join("");
}

export function getDecadeDetailCardMarkup(
	decade: HistoricalTimelineDecade,
	labels: DecadeColumnLabels,
	closeLabel: string,
	yearRange: string,
	isSingleYear = false
): string {
	const decadeName = decade.name ?? "";
	const exploreClassName = `historical-timeline__explore${isSingleYear ? " is-single-year" : ""}`;

	return `
		<div class="historical-timeline__detail-card-inner">
			${getHoverImageMarkup(decade.image, decadeName)}
			<div class="historical-timeline__decade-hover-overlay"></div>
			<div class="historical-timeline__detail-card-metrics">
				<div class="historical-timeline__decade-hover-graphs-host" data-decade-graphs-host></div>
				<div class="historical-timeline__detail-card-facts" data-decade-facts-host></div>
			</div>
			<div class="historical-timeline__decade-hover-content">
				<div class="historical-timeline__detail-card-header">
					<button
						type="button"
						class="historical-timeline__detail-close"
						aria-label="${closeLabel}"
					>
						<span class="historical-timeline__detail-close-icon" aria-hidden="true"></span>
					</button>
					<div class="historical-timeline__decade-hover-grid historical-timeline__decade-hover-grid--detail">
						<div class="historical-timeline__decade-hover-side historical-timeline__decade-hover-side--detail">
							<div class="${exploreClassName}">
								<span class="historical-timeline__explore-line" id="historical-timeline-detail-decade-label">${labels.explore ?? "Explore"}</span>
								<span class="historical-timeline__explore-years">${yearRange}</span>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	`.trim();
}

export function getDecadeColumnMarkup({
	decade,
	decadeIndex,
	labels,
}: DecadeColumnMarkupParams): string {
	const decadeName = decade.name ?? "";
	const decadeAttr = decade.name ? ` data-decade="${decade.name}"` : "";
	const decadeLabel = decadeName
		? `${decadeName}, ${labels.explore ?? "Explore"}`
		: (labels.explore ?? "Explore");

	return `
		<div
			class="historical-timeline__decade"
			data-decade-index="${decadeIndex}"${decadeAttr}
			role="button"
			tabindex="0"
			aria-label="${decadeLabel}"
		>
			<div class="historical-timeline__decade-default">
				<div class="historical-timeline__divider"></div>
				<div class="historical-timeline__decade-body">
					<span class="historical-timeline__decade-title">${decadeName}</span>
				</div>
			</div>

			<div class="historical-timeline__decade-hover">
				${getHoverImageMarkup(getDecadeHoverImageUrl(decade), decadeName)}
				<div class="historical-timeline__decade-hover-overlay"></div>
				<div class="historical-timeline__detail-card-metrics">
					<div class="historical-timeline__decade-hover-graphs-host" data-decade-graphs-host></div>
					<div class="historical-timeline__detail-card-facts" data-decade-facts-host></div>
				</div>
				<div class="historical-timeline__decade-hover-content">
					<div class="historical-timeline__divider"></div>
					<div class="historical-timeline__decade-hover-grid">
						<div class="historical-timeline__decade-hover-side">
							<div class="historical-timeline__explore">
								<span data-explore-line class="historical-timeline__explore-line">${labels.explore ?? "Explore"}</span>
								<span data-years-line class="historical-timeline__explore-years"></span>
							</div>
							<div class="historical-timeline__decade-hover-dots"></div>
						</div>
					</div>
				</div>
			</div>

			<div class="historical-timeline__select-label" aria-hidden="true">
				<div class="historical-timeline__select-label-content">${labels.select ?? "Select"}</div>
			</div>
		</div>
	`.trim();
}

export function renderDecadeColumn(params: DecadeColumnMarkupParams): DecadeColumnElements {
	const container = document.createElement("div");
	container.innerHTML = getDecadeColumnMarkup(params);

	const column = container.firstElementChild as HTMLElement;
	const defaultState = column.querySelector<HTMLElement>(".historical-timeline__decade-default");
	const defaultDivider = defaultState?.querySelector<HTMLElement>(".historical-timeline__divider");
	const defaultBody = defaultState?.querySelector<HTMLElement>(".historical-timeline__decade-body");
	const title = defaultBody?.querySelector<HTMLElement>(".historical-timeline__decade-title");
	const hoverCard = column.querySelector<HTMLElement>(".historical-timeline__decade-hover");
	const exploreLabel = hoverCard?.querySelector<HTMLElement>(".historical-timeline__explore");
	const hoverDotsHost = hoverCard?.querySelector<HTMLElement>(".historical-timeline__decade-hover-dots");
	const graphsHost = hoverCard?.querySelector<HTMLElement>("[data-decade-graphs-host]");
	const factsHost = hoverCard?.querySelector<HTMLElement>("[data-decade-facts-host]");
	const selectLabel = column.querySelector<HTMLElement>(".historical-timeline__select-label");
	const selectContent = selectLabel?.querySelector<HTMLElement>(".historical-timeline__select-label-content");

	if (
		!defaultDivider ||
		!defaultBody ||
		!title ||
		!hoverCard ||
		!exploreLabel ||
		!hoverDotsHost ||
		!graphsHost ||
		!factsHost ||
		!selectLabel ||
		!selectContent
	) {
		throw new Error("Historical timeline decade column markup is missing required elements.");
	}

	return {
		column,
		defaultDivider,
		defaultBody,
		title,
		hoverCard,
		exploreLabel,
		hoverDotsHost,
		graphsHost,
		factsHost,
		selectLabel,
		selectContent,
	};
}
